import { DrawerRow, DrawerSection } from "@/components/side-drawer";
import { Badge } from "@/components/ui/badge";

interface HasCaptured {
    clickIds: Record<string, string> | null;
    utm: Record<string, string> | null;
    sources: string[];
    primarySource: string | null;
    country: string | null;
    landingUrl: string | null;
    referrer: string | null;
    ip: string | null;
    userAgent: string | null;
    visitorId: string | null;
    metadata: Record<string, unknown> | null;
}

/**
 * Shared rendering of the "captured data" section for both leads and visits.
 * Both entities collect the same attribution + device info; this keeps the
 * admin UI consistent.
 */
export function CapturedData({ record }: { record: HasCaptured }): React.ReactNode {
    return (
        <>
            <DrawerSection title="Attribution">
                <DrawerRow
                    label="Primary source"
                    value={
                        record.primarySource ? (
                            <Badge variant="muted">{record.primarySource}</Badge>
                        ) : (
                            "—"
                        )
                    }
                    mono={false}
                />
                <DrawerRow
                    label="All sources"
                    value={record.sources.length > 0 ? record.sources.join(", ") : "—"}
                />
                <DrawerRow label="Country" value={record.country ?? "—"} />
                <DrawerRow label="Landing URL" value={record.landingUrl ?? "—"} />
                <DrawerRow label="Visitor id" value={record.visitorId ?? "—"} />
            </DrawerSection>

            <DrawerSection title="Click IDs">
                {record.clickIds && Object.keys(record.clickIds).length > 0 ? (
                    Object.entries(record.clickIds).map(([k, v]) => (
                        <DrawerRow key={k} label={k} value={String(v)} />
                    ))
                ) : (
                    <p className="text-xs text-muted-foreground">none</p>
                )}
            </DrawerSection>

            <DrawerSection title="UTM">
                {record.utm && Object.keys(record.utm).length > 0 ? (
                    Object.entries(record.utm).map(([k, v]) => (
                        <DrawerRow key={k} label={k} value={String(v)} />
                    ))
                ) : (
                    <p className="text-xs text-muted-foreground">none</p>
                )}
            </DrawerSection>

            <DrawerSection title="Request">
                <DrawerRow label="IP" value={record.ip ?? "—"} />
                <DrawerRow label="Referrer" value={record.referrer ?? "—"} />
                <DrawerRow label="User-Agent" value={record.userAgent ?? "—"} />
            </DrawerSection>

            {record.metadata && Object.keys(record.metadata).length > 0 && (
                <DrawerSection title="Metadata">
                    <pre
                        data-testid="record-metadata"
                        className="max-h-60 overflow-auto rounded bg-muted p-3 font-mono text-[11px]"
                    >
                        {JSON.stringify(record.metadata, null, 2)}
                    </pre>
                </DrawerSection>
            )}
        </>
    );
}
